import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Play, Pause, Stop, ArrowLeft, Fire } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/tyj7q2ct_logo%20365.png";
const MACHINE_IMAGE = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/3k3p2knk_other%20365%20image.png";

const CARDIO_TYPES = [
  { id: 'walking', label: 'Walking' },
  { id: 'running', label: 'Running' },
  { id: 'treadmill', label: 'Treadmill' },
  { id: 'cycling', label: 'Cycling' },
  { id: 'elliptical', label: 'Elliptical' },
  { id: 'stair_stepper', label: 'Stair Stepper' },
  { id: 'dance_cardio', label: 'Dance Cardio' },
  { id: 'jump_rope', label: 'Jump Rope' },
  { id: 'swimming', label: 'Swimming' },
  { id: 'rowing', label: 'Rowing' },
  { id: 'hiit', label: 'HIIT' },
];

const Timer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  
  const [cardioType, setCardioType] = useState(location.state?.cardioType || 'running');
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning]);

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleStart = () => {
    setIsRunning(true);
    setHasStarted(true);
  };

  const handlePause = () => setIsRunning(false);
  const handleResume = () => setIsRunning(true);

  const handleFinish = async () => {
    setIsRunning(false);
    const durationMinutes = Math.max(1, Math.round(elapsedSeconds / 60));
    const caloriesPerMinute = 8;
    const estimatedCalories = Math.round(durationMinutes * caloriesPerMinute);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/cardio-logs`, {
        cardio_type: cardioType,
        duration_minutes: durationMinutes,
        calories_burned: estimatedCalories,
        date: new Date().toISOString().split('T')[0],
      }, { withCredentials: true, headers });

      navigate('/session-complete', { 
        state: { 
          log: {
            cardio_type: cardioType,
            duration_minutes: durationMinutes,
            calories_burned: estimatedCalories,
            elapsed_seconds: elapsedSeconds,
          }
        }
      });
    } catch (error) {
      toast.error('Failed to save session');
    }
  };

  const estimatedCalories = Math.round((elapsedSeconds / 60) * 8);

  return (
    <div className="min-h-screen bg-background flex flex-col relative overflow-hidden" data-testid="timer-screen">
      {/* Background logo */}
      <div 
        className="absolute inset-0 opacity-[0.05] bg-center bg-no-repeat bg-contain pointer-events-none"
        style={{ backgroundImage: `url(${LOGO_URL})` }}
      />

      {/* Machine image overlay */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-1/3 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `url(${MACHINE_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
        }}
      />
      <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-background to-transparent" />

      {/* Header */}
      <header className="px-6 py-4 relative z-10">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <button 
            onClick={() => {
              if (hasStarted) {
                if (window.confirm('Are you sure you want to leave? Your session will be lost.')) {
                  navigate(-1);
                }
              } else {
                navigate(-1);
              }
            }}
            className="w-10 h-10 rounded-full bg-card flex items-center justify-center"
            data-testid="back-btn"
          >
            <ArrowLeft weight="bold" className="w-5 h-5 text-foreground" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {hasStarted ? 'Session Active' : 'Start Session'}
            </h1>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-12 relative z-10">
        <div className="max-w-md w-full text-center">
          {/* Cardio Type Selector */}
          {!hasStarted && (
            <div className="mb-12 animate-fade-in-up">
              <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-4">Select Cardio Type</p>
              <Select value={cardioType} onValueChange={setCardioType}>
                <SelectTrigger className="bg-card border-[#D4AF37]/30 rounded-xl py-6 text-lg" data-testid="cardio-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CARDIO_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Active Cardio Type Display */}
          {hasStarted && (
            <div className="mb-8">
              <p className="text-sm text-muted-foreground uppercase tracking-wider">Current Session</p>
              <p className="text-2xl font-bold text-[#D4AF37] capitalize" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {cardioType.replace('_', ' ')}
              </p>
            </div>
          )}

          {/* Timer Display */}
          <div className="relative mb-12">
            {isRunning && (
              <div className="absolute inset-0 bg-[#D4AF37]/20 blur-[100px] rounded-full animate-pulse" />
            )}
            <div 
              className={`relative timer-display text-7xl sm:text-8xl font-black tracking-tighter ${isRunning ? 'text-[#D4AF37]' : 'text-foreground'}`}
              data-testid="timer-display"
            >
              {formatTime(elapsedSeconds)}
            </div>
          </div>

          {/* Stats during session */}
          {hasStarted && (
            <div className="flex justify-center gap-8 mb-12">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Fire weight="fill" className="w-5 h-5 text-orange-500" />
                  <span className="text-2xl font-bold text-foreground">{estimatedCalories}</span>
                </div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Est. Calories</p>
              </div>
            </div>
          )}

          {/* Control Buttons */}
          <div className="flex justify-center gap-4">
            {!hasStarted ? (
              <Button
                onClick={handleStart}
                className="w-20 h-20 rounded-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] transition-all gold-glow gold-glow-hover"
                data-testid="start-btn"
              >
                <Play weight="fill" className="w-10 h-10" />
              </Button>
            ) : (
              <>
                {isRunning ? (
                  <Button
                    onClick={handlePause}
                    className="w-20 h-20 rounded-full bg-card border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
                    data-testid="pause-btn"
                  >
                    <Pause weight="fill" className="w-10 h-10" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleResume}
                    className="w-20 h-20 rounded-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] transition-all gold-glow"
                    data-testid="resume-btn"
                  >
                    <Play weight="fill" className="w-10 h-10" />
                  </Button>
                )}
                <Button
                  onClick={handleFinish}
                  className="w-20 h-20 rounded-full bg-green-600 text-white hover:bg-green-500 transition-all"
                  data-testid="finish-btn"
                >
                  <Stop weight="fill" className="w-10 h-10" />
                </Button>
              </>
            )}
          </div>

          {hasStarted && !isRunning && elapsedSeconds > 0 && (
            <p className="mt-6 text-sm text-muted-foreground">
              Press the green button to finish and save your session
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

export default Timer;
